/** Espera antes de tentar de novo o import dinâmico de uma tela que falhou. */
export const CHUNK_RETRY_DELAY_MS = 500

/** Marca na sessionStorage: a página já recarregou uma vez, nesta sessão, por causa do chunk. */
export const CHUNK_RELOAD_KEY_PREFIX = 'gestao.chunk-reload.'

/** O que o `lazyWithRetry` usa do navegador (trocado nos testes). */
export interface ChunkRetryEnv {
  wait: (ms: number) => Promise<void>
  reload: () => void
  /** `null` quando o navegador não deixa usar a sessionStorage. */
  storage: () => Pick<Storage, 'getItem' | 'setItem'> | null
}

const browserEnv: ChunkRetryEnv = {
  wait: (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
  reload: () => window.location.reload(),
  storage: () => window.sessionStorage,
}

/**
 * Import dinâmico de uma tela (chunk do build) com uma segunda chance: rede que oscila
 * (`net::ERR_NETWORK_CHANGED`) ou deploy novo que apagou o chunk antigo. Falhou, tenta de novo
 * depois de 500 ms; falhou outra vez e é a primeira vez nesta sessão para aquele chunk, recarrega a
 * página uma vez (o HTML novo aponta para os chunks atuais). Senão, ou sem sessionStorage (sem
 * como garantir uma vez só), a falha segue para o `errorElement` da rota ("Esta tela não abriu").
 */
export async function lazyWithRetry<T>(
  chunk: string,
  load: () => Promise<T>,
  env: ChunkRetryEnv = browserEnv,
): Promise<T> {
  try {
    return await load()
  } catch {
    await env.wait(CHUNK_RETRY_DELAY_MS)
  }
  try {
    return await load()
  } catch (error) {
    if (!firstReload(chunk, env)) throw error
    env.reload()
    // A página está recarregando: a rota segue em carregamento até lá, sem piscar o erro.
    return new Promise<T>(() => {})
  }
}

/** Grava a marca do chunk e diz se é a primeira vez nesta sessão. */
function firstReload(chunk: string, env: ChunkRetryEnv): boolean {
  const key = `${CHUNK_RELOAD_KEY_PREFIX}${chunk}`
  try {
    const storage = env.storage()
    if (!storage || storage.getItem(key) !== null) return false
    storage.setItem(key, '1')
    return true
  } catch {
    return false
  }
}
